import React, { ReactNode } from 'react';

import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { color } from '@styles/themes';
import { Typography as ATypography, TypographyProps } from 'antd';
import { tuple, tupleNum } from 'antd/lib/_util/type';
import { ParagraphProps } from 'antd/lib/typography/Paragraph';
import { TextProps } from 'antd/lib/typography/Text';
import { TitleProps } from 'antd/lib/typography/Title';
import { transparentize } from 'polished';

const { Title: ATitle, Paragraph: AParagraph, Text: AText } = ATypography;

export const tYzTypeTypography = tuple('HEAD', 'BODY');
export const tYzTypeTypographyText = tuple(
  'title',
  'basic',
  'button',
  'symptom',
  'scale',
  'term-sub-title',
  'term-content',
);
export const tLevel = tupleNum(1, 2, 3, 4, 5);

export type TYzTypeTypography = (typeof tYzTypeTypography)[number] | undefined;
export type TYzTypeTypographyText = (typeof tYzTypeTypographyText)[number] | undefined;

type TLevel = (typeof tLevel)[number] | undefined;

type TStyledTypographyParams = {
  yzTypeTypography?: TYzTypeTypography;
  marginBottom?: string;
  marginTop?: string;
};

export type TTypographyParams = TypographyProps &
  TStyledTypographyParams & {
    children: any;
  };

export interface ITitleParams extends TitleProps {
  level: TLevel;
  icon?: React.ReactNode;
  iconStyle?: any;
  isBold?: boolean;
}

export interface IParagraphParams extends ParagraphProps {}

type TStyledTypographyTextParams = TextProps & {
  yzTypeTypographyText?: TYzTypeTypographyText;
  fontSize?: string;
  color?: string;
  fontWeight?: number;
  lineHeight?: string;
  fontStyle?: string;
  letterSpacing?: string;
  textDecoration?: string;
};

export type ITextParams = {
  children?: ReactNode;
} & TStyledTypographyTextParams &
  TextProps;

const handleTypographyWithType = (yzTypeTypography: TYzTypeTypography) => {
  switch (yzTypeTypography) {
    case 'HEAD':
      return css`
        padding-left: 11px;
      `;
    case 'BODY':
      return css`
        padding-left: 9px;
      `;
    default:
      return '';
  }
};

const StyledTypography = styled(ATypography, { shouldForwardProp: isPropValid })<TStyledTypographyParams>`
  ${({ yzTypeTypography }) => handleTypographyWithType(yzTypeTypography)}
  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${marginBottom};
    `}
  ${({ marginTop }) =>
    marginTop &&
    css`
      margin-top: ${marginTop};
    `}
`;

export const Typography = ({ children, ...rest }: TTypographyParams) => (
  <StyledTypography {...rest}>{children}</StyledTypography>
);

const StyledTypographyTitle = styled(ATitle, { shouldForwardProp: isPropValid })<ITitleParams>`
  font-weight: ${({ isBold }) => (isBold ? 'bold !important' : 'normal !important')};
  color: ${color.infoText};
  margin-bottom: 0;
  ${({ level }) => {
    switch (level) {
      case 1:
        return css`
          font-size: 27px !important;
        `;
      case 2:
        return css`
          font-size: 24px !important;
        `;
      case 3:
        return css`
          font-size: 20px !important;
        `;
      case 4:
        return css`
          font-size: 18px !important;
        `;
      case 5:
        return css`
          font-size: 16px !important;
        `;
      default:
        return css``;
    }
  }}
`;

export const TypographyTitle = ({ children, icon, iconStyle, level = 1, ...props }: ITitleParams) => (
  <StyledTypographyTitle {...props} level={level < 5 ? level : 4}>
    <span style={iconStyle}>{icon}</span>
    <span>{children}</span>
  </StyledTypographyTitle>
);

const StyledTypographyParagraph = styled(AParagraph, { shouldForwardProp: isPropValid })<IParagraphParams>`
  margin-bottom: 0 !important;
  font-size: 16px;
  color: ${color.infoText};
  white-space: pre-line;
  word-break: keep-all;
`;

export const TypographyParagraph = ({ children, ...rest }: IParagraphParams) => (
  <StyledTypographyParagraph {...rest}>{children}</StyledTypographyParagraph>
);

const handleTypographyTextWithType = (yzTypeTypographyText: TYzTypeTypographyText) => {
  switch (yzTypeTypographyText) {
    case 'title':
      return css`
        font-size: 24px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 36px;
      `;
    case 'term-sub-title':
      return css`
        font-size: 16px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 16px;
      `;
    case 'term-content':
      return css`
        font-size: 15px;
        font-weight: 400;
        letter-spacing: -0.01em;
        line-height: 21px;
      `;
    case 'symptom':
      return css`
        font-size: 16px;
        font-weight: 600;
        letter-spacing: -0.01em;
        line-height: 21px;
      `;
    case 'basic':
      return css`
        font-size: 15px;
        font-weight: 400;
        letter-spacing: -0.01em;
        line-height: 21px;
      `;
    case 'scale':
      return css`
        font-size: 15px;
        font-weight: 400;
        letter-spacing: -0.01em;
        line-height: 1.2em;
      `;
    case 'button':
      return css`
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.05em;
        line-height: 19px;

        :disabled {
          border: none;
          color: ${transparentize(0.3, color.disabled)};
        }
      `;
    default:
      return '';
  }
};

const StyledTypographyText = styled(AText, { shouldForwardProp: isPropValid })<TStyledTypographyTextParams>`
  margin: 0 !important;
  color: ${color.infoText};
  font-size: 16px;

  ${({ yzTypeTypographyText }) => handleTypographyTextWithType(yzTypeTypographyText)}

  ${({ fontSize }) => css`
    font-size: ${fontSize} !important;
  `}
  ${({ color: customColor }) => css`
    color: ${customColor} !important;
  `}
  ${({ fontWeight }) => css`
    font-weight: ${fontWeight};
  `}
  ${({ lineHeight }) => css`
    line-height: ${lineHeight};
  `}
  ${({ fontStyle }) => css`
    font-style: ${fontStyle};
  `}
  ${({ letterSpacing }) => css`
    letter-spacing: ${letterSpacing};
  `}
  ${({ textDecoration }) => css`
    text-decoration: ${textDecoration};
  `}
`;

export const TypographyText = ({ children, ...rest }: ITextParams) => (
  <StyledTypographyText {...rest}>{children}</StyledTypographyText>
);
